<template>
  <Transition
    :enter-active-class
    :leave-active-class
    name="v-transition-collapse"
    @after-enter="afterEnter"
    @after-leave="afterLeave"
    @before-enter="beforeEnter"
    @before-leave="beforeLeave"
    @enter="enter"
    @leave="leave"
  >
    <div v-if="!lazy || rendered" v-show="visible" ref="el" class="v-transition-collapse">
      <slot />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'

import { useResizeObserver } from '../composables/useResizeObserver.ts'
import { nextFrame } from '../utils/helpers.ts'

interface Props {
  fade?: boolean
  visible?: boolean
  lazy?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  fade: true,
  visible: false,
  lazy: false,
})

defineSlots<{
  default?(): any
}>()

const rendered = ref(false)
const el = useTemplateRef('el')
const lastHeight = ref(0)

useResizeObserver(el, ({ height }) => (lastHeight.value = height))

const enterActiveClass = computed(() => {
  let string = 'v-transition-collapse--enter-active'
  if (props.fade) string += ' v-transition-collapse--fade'

  return string
})

const leaveActiveClass = computed(() => {
  let string = 'v-transition-collapse--leave-active'
  if (props.fade) string += ' v-transition-collapse--fade'

  return string
})

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      rendered.value = true
    }
  },
  { immediate: true },
)

async function beforeEnter(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return
  }

  await nextFrame()

  // It seems that the child component is visible when this component gets removed (and later re-added) while nextFrame is running.
  // When visible is false, this should just cancel to prevent the css display value from resetting.
  if (!props.visible) {
    return
  }

  if (!element.style.height) {
    element.style.height = '0px'
  }

  element.style.display = ''
}

async function enter(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return
  }

  await nextFrame()
  await nextFrame()

  element.style.height = `${element.scrollHeight}px`
}

function afterEnter(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return
  }
  element.style.height = ''
}

async function beforeLeave(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return
  }
  await nextFrame()

  if (!element.style.height) {
    element.style.height = `${lastHeight.value}px`
  }
}

async function leave(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return
  }

  await nextFrame()
  await nextFrame()

  element.style.height = '0px'
}

function afterLeave(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return
  }

  element.style.height = ''
}
</script>

<style>
.v-transition-collapse--fade.v-transition-collapse-enter,
.v-transition-collapse--fade.v-transition-collapse-leave-to {
  opacity: 0 !important;
}

.v-transition-collapse--enter-active,
.v-transition-collapse--leave-active {
  overflow: hidden;
  transition:
    opacity var(--transition),
    height var(--transition);
}
</style>
